/**
 * The postal code reference data, as the two endpoints that manage it report it.
 *
 * **There is deliberately no `PostalCode` entity here.** The table is several hundred thousand rows
 * of published reference data, addressed only by (country, code) and read only by the service's own
 * address parse. It exposes no endpoint that returns a row, no search and no CRUD - so nothing in
 * this app can hold one, and neither type below extends BaseEntity. What an administrator can see
 * is how much is loaded, and what happened the last time it was loaded.
 *
 * The data is CC BY 4.0, GeoNames. Anything built on these types has to carry that attribution.
 */

/**
 * How many codes are held for one country, from `GET /postal-code/coverage`.
 *
 * A country **absent** from the coverage list is one no postcode will ever be read for - which is
 * why the page joins this against the country list rather than rendering it alone.
 */
export interface PostalCodeCoverage {
  /**
   * ISO alpha-2, matching `Country.alpha2`. The only thing a coverage row identifies a country by:
   * it is grouped in the database over the code column, so there is no id and no name to join on.
   */
  countryCode: string;
  codes: number;
}

/**
 * What one run of `POST /postal-code/import` did.
 *
 * Every field is worth showing. Reducing this to "Success" is how somebody would fail to notice
 * that they pointed `cas.postal-code.source-file` at the wrong file - see `rowsSkippedUnreadable`.
 */
export interface PostalCodeImportSummary {
  /** The path the server read, echoed back so an admin can confirm which file it was. */
  file: string;
  /** Countries the file had at least one code for **and** that the country table has. */
  countriesLoaded: number;
  /** Non-blank lines in the file. */
  rowsRead: number;
  /** Rows written. */
  rowsLoaded: number;
  /**
   * Rows for a country this database does not have. **Expected to be very large and not an error** -
   * the combined export carries every country GeoNames publishes, and this database has a few dozen.
   */
  rowsSkippedUnknownCountry: number;
  /** Malformed lines. A handful is normal; thousands means the wrong file. */
  rowsSkippedUnreadable: number;
}

/**
 * How many unreadable rows stop being unremarkable.
 *
 * A round number rather than a measured one: the point is only to put a warning next to a count
 * that says "wrong file" instead of "a few ragged lines", and the file has a million-odd rows in it.
 */
export const UNREADABLE_ROWS_WORTH_REPORTING = 1000;
